'use client'

import Link from 'next/link'
import { useMemo, useState } from 'react'

import type { JobFilterOptions } from './actions'
import type { JobPosting } from './jobs-data'

type FilterKey = keyof JobFilterOptions

function FilterDropdown({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string
  options: string[]
  selected: string[]
  onToggle: (value: string) => void
}) {
  return (
    <details className="relative group">
      <summary className="list-none cursor-pointer bg-black border border-zinc-700 text-zinc-300 text-xs uppercase tracking-wider px-3 py-3 flex items-center justify-between">
        <span>
          {label}
          {selected.length > 0 ? ` (${selected.length})` : ''}
        </span>
        <span className="text-zinc-500">+</span>
      </summary>

      <div className="absolute left-0 right-0 mt-2 z-20 border border-zinc-700 bg-black p-2">
        <div className="max-h-40 overflow-y-auto pr-1 space-y-1">
          {options.map((option) => {
            const isChecked = selected.includes(option)

            return (
              <label
                key={option}
                className="flex items-center gap-2 px-2 py-2 text-xs text-zinc-300 hover:bg-zinc-900 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => onToggle(option)}
                  className="accent-[#25B362]"
                />
                <span>{option}</span>
              </label>
            )
          })}
        </div>
      </div>
    </details>
  )
}

export default function JobsPageClient({
  initialFilterOptions,
  jobs,
}: {
  initialFilterOptions: JobFilterOptions
  jobs: JobPosting[]
}) {
  const [selectedFilters, setSelectedFilters] = useState<Record<FilterKey, string[]>>({
    category: [],
    technology: [],
    region: [],
    payment: [],
  })
  const [searchQuery, setSearchQuery] = useState('')

  const toggleFilterValue = (key: FilterKey, value: string) => {
    setSelectedFilters((prev) => {
      const isSelected = prev[key].includes(value)

      return {
        ...prev,
        [key]: isSelected ? prev[key].filter((item) => item !== value) : [...prev[key], value],
      }
    })
  }

  const filteredJobs = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase()

    return jobs.filter((job) => {
      const matchesSearch =
        normalizedQuery.length === 0 ||
        [job.title, job.company, job.category, job.technology, job.region, job.role]
          .join(' ')
          .toLowerCase()
          .includes(normalizedQuery)

      const matchesCategory =
        selectedFilters.category.length === 0 || selectedFilters.category.includes(job.category)
      const matchesTechnology =
        selectedFilters.technology.length === 0 ||
        selectedFilters.technology.includes(job.technology)
      const matchesRegion =
        selectedFilters.region.length === 0 || selectedFilters.region.includes(job.region)
      const matchesPayment =
        selectedFilters.payment.length === 0 || selectedFilters.payment.includes(job.payment)

      return (
        matchesSearch && matchesCategory && matchesTechnology && matchesRegion && matchesPayment
      )
    })
  }, [searchQuery, selectedFilters])

  return (
    <main className="bg-black text-white min-h-screen px-4 py-12 lg:py-16">
      <div className="max-w-6xl mx-auto">
        <header className="mb-10 lg:mb-12 text-center">
          <h1 className="text-xs uppercase tracking-[0.12em] text-zinc-400 mb-3">Cryptocurrency jobs</h1>
          <h2 className="text-3xl lg:text-5xl font-bold font-[family-name:var(--font-saira)] leading-tight">
            Find your next crypto job
          </h2>
        </header>

        <section className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(280px,1fr)] gap-6 lg:gap-8">
          <div className="border border-zinc-800 bg-zinc-950/70">
            <div className="px-5 py-6 lg:px-8 lg:py-8 border-b border-zinc-800">
              <div className="max-w-2xl mx-auto">
                <label htmlFor="jobs-search" className="sr-only">
                  Search jobs
                </label>
                <div className="flex items-center border border-zinc-700 focus-within:border-[#25B362] transition-colors bg-black/70">
                  <input
                    id="jobs-search"
                    type="text"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="Search role, company, or keyword"
                    className="w-full bg-transparent px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-500 outline-none"
                  />
                  <span className="px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-400 border-l border-zinc-700">
                    Live
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
                  <FilterDropdown
                    label="Category"
                    options={initialFilterOptions.category}
                    selected={selectedFilters.category}
                    onToggle={(value) => toggleFilterValue('category', value)}
                  />
                  <FilterDropdown
                    label="Technology"
                    options={initialFilterOptions.technology}
                    selected={selectedFilters.technology}
                    onToggle={(value) => toggleFilterValue('technology', value)}
                  />
                  <FilterDropdown
                    label="Region"
                    options={initialFilterOptions.region}
                    selected={selectedFilters.region}
                    onToggle={(value) => toggleFilterValue('region', value)}
                  />
                  <FilterDropdown
                    label="Payment"
                    options={initialFilterOptions.payment}
                    selected={selectedFilters.payment}
                    onToggle={(value) => toggleFilterValue('payment', value)}
                  />
                </div>
              </div>
            </div>

            <div className="divide-y divide-zinc-800">
              {filteredJobs.length === 0 && (
                <div className="px-5 py-8 lg:px-8 text-sm text-zinc-400">
                  No jobs match your current filters.
                </div>
              )}

              {filteredJobs.map((job) => (
                <Link
                  key={job.slug}
                  href={`/jobs/${job.slug}`}
                  className="block px-5 py-5 lg:px-8 hover:bg-zinc-900/70 transition-colors"
                >
                  <article>
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="text-[10px] uppercase tracking-widest text-[#25B362]">
                        {job.category}
                      </span>
                      <span className="text-zinc-500">·</span>
                      <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                        {job.region}
                      </span>
                    </div>
                    <h2 className="text-lg lg:text-xl font-semibold font-[family-name:var(--font-saira)] text-white mb-1">
                      {job.title}
                    </h2>
                    <p className="text-zinc-400 text-sm">{job.company}</p>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs">
                      <span className="border border-zinc-700 px-2 py-1 text-zinc-300">
                        {job.technology}
                      </span>
                      <span className="border border-zinc-700 px-2 py-1 text-zinc-300">
                        {job.payment}
                      </span>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </div>

          <aside className="border border-zinc-800 bg-zinc-950/70 p-5 lg:p-6 h-fit">
            <p className="text-xs uppercase tracking-[0.12em] text-zinc-400 mb-4">Hiring pulse</p>
            <h2 className="text-2xl font-bold font-[family-name:var(--font-saira)] mb-5 leading-tight">
              Teams actively hiring this week
            </h2>

            <ul className="space-y-3 mb-6">
              {[
                'Trading Infrastructure',
                'DeFi Product',
                'Growth & Community',
                'Research / Analytics',
              ].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm text-zinc-300">
                  <span className="w-1.5 h-1.5 bg-[#5841D8]" />
                  {item}
                </li>
              ))}
            </ul>

            <Link
              href="/jobs/post"
              className="block w-full text-center border border-[#25B362] text-[#25B362] py-3 text-xs uppercase tracking-widest font-bold hover:bg-[#25B362] hover:text-black transition-all duration-300"
            >
              Post a job
            </Link>
          </aside>
        </section>
      </div>
    </main>
  )
}
